export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface PagedQuery {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
  search?: string;
}

/** SAP synchronization state. Mirrors KrosnoEDI.Domain.Enums.SapSyncStatus. */
export enum SapSyncStatus {
  NotSynced = 0,
  Queued = 1,
  Synced = 2,
}

export interface PurchaseOrderList {
  id: string;
  amazonPurchaseOrderNumber: string;
  channel: number;
  status: number;
  orderDate: string;
  expectedDeliveryDate: string | null;
  totalAmount: number;
  currency: string;
  itemCount: number;
  createdAt: string;
  fulfillmentChannel: string | null;
  marketplace: string | null;
  shipToCountry: string | null;
  isBusinessOrder: boolean;
  firstItemTitle: string | null;
  /** SAP sync state (0/1/2). Drives the SAP column badge. */
  sapSyncStatus: SapSyncStatus;
  sapQueuedAt: string | null;
  sapSyncedAt: string | null;
}

export interface PurchaseOrderDetail {
  id: string;
  amazonPurchaseOrderNumber: string;
  channel: number;
  status: number;
  orderDate: string;
  expectedDeliveryDate: string | null;
  shipWindowStart: string | null;
  shipWindowEnd: string | null;
  sellingPartyId: string | null;
  shipToPartyId: string | null;
  shipToAddress: string | null;
  billToPartyId: string | null;
  totalAmount: number;
  currency: string;
  syncedAt: string | null;
  createdAt: string;
  /** SAP sync state — drives the detail-page status row + button enabled state. */
  sapSyncStatus: SapSyncStatus;
  sapQueuedAt: string | null;
  sapSyncedAt: string | null;
  sapQueuedBy: string | null;
  marketplace: string | null;
  fulfillmentChannel: string | null;
  items: PurchaseOrderItem[];
  confirmations: ConfirmationList[];
  shipments: ShipmentList[];
  invoices: InvoiceList[];
}

export interface PurchaseOrderItem {
  id: string;
  itemSequenceNumber: number;
  amazonProductId: string;
  asin: string | null;
  sku: string | null;
  externalId: string | null;
  productTitle: string;
  orderedQuantity: number;
  quantityUnitOfMeasure: string;
  unitPrice: number;
  totalPrice: number;
  confirmedQuantity: number | null;
  shippedQuantity: number | null;
}

export interface ConfirmationList {
  id: string;
  status: string;
  confirmationDate: string;
  confirmedBy: string | null;
}

export interface ShipmentList {
  id: string;
  amazonShipmentId: string | null;
  status: string;
  shipDate: string | null;
  carrierName: string | null;
  trackingNumber: string | null;
}

export interface InvoiceList {
  id: string;
  invoiceNumber: string;
  purchaseOrderNumber: string | null;
  invoiceType: string;
  status: string;
  totalAmount: number;
  currency: string;
  invoiceDate: string;
  hasPdf: boolean;
}

export interface ShipmentDetail {
  id: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  amazonShipmentId: string | null;
  status: number;
  shipDate: string | null;
  estimatedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  carrierName: string | null;
  carrierScac: string | null;
  trackingNumber: string | null;
  billOfLadingNumber: string | null;
  shipFromAddress: string | null;
  shipToAddress: string | null;
  totalCartons: number;
  totalWeight: number;
  weightUnit: string;
  totalVolume: number;
  volumeUnit: string;
  createdAt: string;
  items: ShipmentItemDto[];
  cartons: ShipmentCartonDto[];
  labels: ShipmentLabelDto[];
  packingDocuments: PackingDocumentDto[];
}

export interface ShipmentItemDto {
  id: string;
  itemSequenceNumber: number;
  amazonProductId: string;
  asin: string | null;
  shippedQuantity: number;
  quantityUnitOfMeasure: string;
}

export interface ShipmentCartonDto {
  id: string;
  cartonId: string;
  cartonSequenceNumber: number;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  weight: number;
  weightUnit: string;
  trackingNumber: string | null;
  sscc: string | null;
  cartonItems: CartonItemDto[];
}

export interface CartonItemDto {
  id: string;
  amazonProductId: string;
  quantity: number;
  expirationDate: string | null;
}

export interface ShipmentLabelDto {
  id: string;
  labelType: string;
  fileName: string;
  contentType: string;
  amazonLabelId: string | null;
  createdAt: string;
}

export interface PackingDocumentDto {
  id: string;
  documentType: string;
  fileName: string;
  contentType: string;
  notes: string | null;
  createdAt: string;
}

export interface InvoiceDetail {
  id: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  invoiceNumber: string;
  amazonInvoiceId: string | null;
  invoiceType: number;
  status: number;
  invoiceDate: string;
  dueDate: string | null;
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  paymentTerms: string | null;
  remitToPartyId: string | null;
  notes: string | null;
  referenceNumber: string | null;
  amazonTransactionId: string | null;
  originalInvoiceId: string | null;
  hasPdf: boolean;
  submittedAt: string | null;
  createdAt: string;
  items: InvoiceItemDto[];
  creditNotes: InvoiceList[];
}

export interface BulkOperationResult {
  totalCount: number;
  successCount: number;
  failedCount: number;
  errors: BulkOperationError[];
}

export interface BulkOperationError {
  entityId: string | null;
  identifier: string;
  error: string;
}

export interface CompanyProfile {
  id: string | null;
  companyName: string;
  taxId: string | null;
  vatEuNumber: string | null;
  street: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  countryCode: string | null;
  amazonVendorCode: string | null;
  remitToPartyId: string | null;
  defaultPaymentTerms: string | null;
  defaultCurrency: string;
  bankName: string | null;
  bankAccountNumber: string | null;
  swiftCode: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
}

export interface InvoiceItemDto {
  id: string;
  itemSequenceNumber: number;
  amazonProductId: string;
  asin: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  taxRate: number;
  taxAmount: number;
  hsCode: string | null;
}

export interface ProductList {
  id: string;
  asin: string;
  sku: string | null;
  ean: string | null;
  title: string;
  brand: string | null;
  category: string | null;
  listPrice: number | null;
  currency: string;
  status: number;
  channel: number;
  availableQuantity: number;
  lastSyncedAt: string | null;
}

export interface ProductDetail {
  id: string;
  asin: string;
  sku: string | null;
  ean: string | null;
  externalId: string | null;
  title: string;
  description: string | null;
  brand: string | null;
  category: string | null;
  costPrice: number | null;
  listPrice: number | null;
  currency: string;
  status: number;
  channel: number;
  imageUrl: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  prices: ProductPriceDto[];
  inventoryRecords: InventoryRecordDto[];
}

export interface ProductPriceDto {
  id: string;
  priceType: string;
  amount: number;
  currency: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export interface InventoryRecordDto {
  id: string;
  productId: string;
  productTitle: string;
  asin: string | null;
  sku: string | null;
  warehouseId: string | null;
  warehouseName: string | null;
  availableQuantity: number;
  reservedQuantity: number | null;
  inboundQuantity: number | null;
  totalFulfillableQuantity: number | null;
  isBackordered: boolean;
  channel: number;
  syncedAt: string | null;
}

export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  role: number;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface DashboardData {
  // Period info
  period: 'week' | 'month' | 'year' | 'custom';
  periodFrom: string;
  periodTo: string;
  timelineGranularity: 'day' | 'week' | 'month';

  // Period-scoped headlines (with delta)
  ordersInPeriod: PeriodStats;
  invoicesInPeriod: PeriodStats;
  shipmentsInPeriod: PeriodStats;
  settlementsInPeriod: PeriodStats;

  // Always-current state
  totalPurchaseOrders: number;
  pendingOrders: number;
  activeShipments: number;
  pendingInvoices: number;
  totalProducts: number;
  lowStockProducts: number;
  totalOrderValue: number;
  totalInvoicedValue: number;
  overdueInvoiceValue: number;
  overdueInvoiceCount: number;

  // Invoice funnel (all-time)
  draftInvoiceCount: number;
  submittedInvoiceCount: number;
  acceptedInvoiceCount: number;
  rejectedInvoiceCount: number;
  paidInvoiceCount: number;

  // SAP funnel (all-time)
  sapNotSyncedCount: number;
  sapQueuedCount: number;
  sapSyncedCount: number;

  // Time series (period-scoped)
  ordersTimeline: TimelinePoint[];
  invoicesTimeline: TimelinePoint[];
  settlementsTimeline: TimelinePoint[];

  // Breakdowns
  invoiceStatusBreakdown: StatusBreakdown[];
  orderStatusBreakdown: StatusBreakdown[];
  ordersByMarketplace: MarketplaceBreakdown[];
  settlementsByMarketplace: MarketplaceBreakdown[];
  ordersByChannel: ChannelBreakdown[];

  // Period-scoped lists
  topProducts: TopProduct[];

  // Recent activity (back-compat, ~10 most recent items mixed)
  recentActivity: RecentActivity[];

  // Sync health per integration
  syncHealth: SyncHealth[];

  // Legacy (kept for back-compat with old chart code)
  invoicesByMonth: InvoiceChartPoint[];
}

export interface PeriodStats {
  count: number;
  value: number;
  previousCount: number;
  previousValue: number;
  countChangePercent: number | null;
  valueChangePercent: number | null;
}

export interface TimelinePoint {
  date: string;
  count: number;
  value: number;
}

export interface MarketplaceBreakdown {
  marketplace: string;
  countryCode: string | null;
  count: number;
  value: number;
}

export interface ChannelBreakdown {
  channel: string;
  count: number;
  value: number;
}

export interface TopProduct {
  sku: string | null;
  asin: string | null;
  productTitle: string;
  orderedQuantity: number;
  revenue: number;
  orderCount: number;
}

export interface SyncHealth {
  source: string;
  lastSuccessfulSync: string | null;
  lastAttempt: string | null;
  lastStatus: string | null;
  recentErrorCount: number;
}

export interface RecentActivity {
  type: string;
  description: string;
  timestamp: string;
  entityId: string | null;
}

export interface InvoiceChartPoint {
  month: string;
  count: number;
  amount: number;
}

export interface StatusBreakdown {
  status: string;
  count: number;
  value: number;
}

export interface FileAttachmentDto {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  description: string | null;
  createdAt: string;
}

export interface SyncLogDto {
  id: string;
  entityType: string;
  entityId: string | null;
  channel: string;
  operation: string;
  status: string;
  errorMessage: string | null;
  durationMs: number;
  createdAt: string;
}

export interface SettlementReportList {
  id: string;
  reportId: string;
  reportType: string | null;
  periodStart: string;
  periodEnd: string;
  marketplace: string;
  totalAmount: number;
  currency: string;
  status: string;
  transactionCount: number;
  /** True when the report has stored CSV available (used for ✓/✗ column and reparse eligibility). */
  hasRawCsv: boolean;
  createdAt: string;
}

export interface SettlementReportDetail extends SettlementReportList {
  /** True when the report has stored CSV available via the /csv endpoint. The CSV
   *  bytes themselves are no longer shipped with the detail response — fetch on demand. */
  hasRawCsv: boolean;
  transactions: SettlementTransaction[];
}

export interface SettlementTransaction {
  id: string;
  transactionDate: string;
  transactionType: string;
  orderId: string | null;
  sku: string | null;
  description: string | null;
  amount: number;
  currency: string;
  marketplace: string | null;
}

// ==================== Amazon Shipments ====================

export interface FulfilledShipmentList {
  id: string;
  amazonOrderId: string;
  shipmentId: string;
  sku: string | null;
  asin: string | null;
  productName: string | null;
  quantityShipped: number;
  purchaseDate: string | null;
  shipmentDate: string | null;
  estimatedArrivalDate: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  shipServiceLevel: string | null;
  recipientName: string | null;
  shipCity: string | null;
  shipCountry: string | null;
  itemPrice: number | null;
  currency: string | null;
  fulfillmentChannel: string | null;
  salesChannel: string | null;
  marketplace: string | null;
  syncedAt: string;
}

export interface FulfilledShipmentDetail extends FulfilledShipmentList {
  merchantOrderId: string | null;
  shipmentItemId: string;
  amazonOrderItemId: string | null;
  paymentsDate: string | null;
  reportingDate: string | null;
  itemTax: number | null;
  shippingPrice: number | null;
  shippingTax: number | null;
  itemPromotionDiscount: number | null;
  shipPromotionDiscount: number | null;
  shipState: string | null;
  shipPostalCode: string | null;
  fulfillmentCenterId: string | null;
  createdAt: string;
}

export interface FulfilledShipmentSummary {
  totalShipments: number;
  totalUnitsShipped: number;
  lastWeekCount: number;
  lastMonthCount: number;
  byCarrier: Record<string, number>;
  byFulfillmentChannel: Record<string, number>;
}

export interface InboundShipmentList {
  id: string;
  shipmentId: string;
  shipmentName: string | null;
  destinationFulfillmentCenterId: string;
  shipmentStatus: string;
  labelPrepType: string | null;
  confirmedNeedByDate: string | null;
  marketplace: string | null;
  itemCount: number;
  totalUnitsShipped: number;
  totalUnitsReceived: number;
  syncedAt: string;
}

export interface InboundShipmentItem {
  id: string;
  sellerSku: string | null;
  fulfillmentNetworkSku: string | null;
  quantityShipped: number;
  quantityReceived: number;
  quantityInCase: number;
}

export interface InboundShipmentDetail extends InboundShipmentList {
  shipFromName: string | null;
  shipFromAddressLine1: string | null;
  shipFromAddressLine2: string | null;
  shipFromCity: string | null;
  shipFromStateOrProvince: string | null;
  shipFromPostalCode: string | null;
  shipFromCountryCode: string | null;
  areCasesRequired: boolean;
  boxContentsSource: string | null;
  estimatedBoxContentsFee: number | null;
  estimatedBoxContentsFeeCurrency: string | null;
  createdAt: string;
  items: InboundShipmentItem[];
}

export interface InboundShipmentSummary {
  totalShipments: number;
  workingCount: number;
  shippedCount: number;
  receivingCount: number;
  closedCount: number;
  byStatus: Record<string, number>;
  byDestinationFc: Record<string, number>;
}

// ==================== Amazon Customer Invoices (VCS VAT) ====================

export interface AmazonCustomerInvoiceList {
  id: string;
  invoiceNumber: string;
  transactionType: string | null;
  invoiceDate: string | null;
  shipmentDate: string | null;
  amazonOrderId: string | null;
  asin: string | null;
  sku: string | null;
  productName: string | null;
  quantityShipped: number;
  currency: string | null;
  totalAmount: number | null;
  itemVatAmount: number | null;
  itemVatRate: number | null;
  buyerName: string | null;
  buyerCountry: string | null;
  marketplace: string | null;
  status: string;
  hasCachedPdf: boolean;
  syncedAt: string;
}

export interface AmazonCustomerInvoiceDetail extends AmazonCustomerInvoiceList {
  transactionEventId: string | null;
  shipmentId: string | null;
  shipmentItemId: string | null;
  itemPriceGross: number | null;
  itemPriceNet: number | null;
  shippingPriceGross: number | null;
  shippingPriceNet: number | null;
  shippingVatAmount: number | null;
  buyerVatNumber: string | null;
  shipToCity: string | null;
  shipToPostalCode: string | null;
  shipToCountry: string | null;
  createdAt: string;
}

export interface AmazonOAuthAccount {
  id: string;
  accountName: string;
  accountType: string;
  country: string | null;
  marketplaceId: string | null;
  isActive: boolean;
  isAuthorized: boolean;
  authorizedAt: string | null;
  sellingPartnerId: string | null;
  lastSyncAt: string | null;
}

export interface AmazonOAuthStartResponse {
  authorizationUrl: string;
  state: string;
}

export interface AmazonOAuthExchangeResult {
  success: boolean;
  accountName: string | null;
  accountId: string | null;
  error: string | null;
}

export interface AmazonCustomerInvoiceSummary {
  totalInvoices: number;
  totalGross: number;
  totalVat: number;
  currency: string;
  newCount: number;
  processedCount: number;
  byCountry: Record<string, number>;
}

// Enum maps for display
export const PurchaseOrderStatusMap: Record<number, string> = {
  0: 'Nowe', 1: 'Potwierdzone odbiór', 2: 'Potwierdzone', 3: 'Częściowo potwierdzone',
  4: 'Odrzucone', 5: 'Wysłane', 6: 'Częściowo wysłane', 7: 'Dostarczone', 8: 'Anulowane', 9: 'Zamknięte'
};

export const ShipmentStatusMap: Record<number, string> = {
  0: 'Szkic', 1: 'Wysłano', 2: 'W transporcie', 3: 'Dostarczono', 4: 'Anulowano'
};

export const InvoiceStatusMap: Record<number, string> = {
  0: 'Szkic', 1: 'Wysłano', 2: 'Zaakceptowano', 3: 'Odrzucono', 4: 'Opłacono', 5: 'Sporny', 6: 'Częściowo opłacono'
};

export const InvoiceTypeMap: Record<number, string> = {
  0: 'Faktura', 1: 'Nota kredytowa', 2: 'Nota debetowa'
};

export const InvoiceStatusStringMap: Record<string, string> = {
  'Draft': 'Szkic', 'Submitted': 'Wysłano', 'Accepted': 'Zaakceptowano',
  'Rejected': 'Odrzucono', 'Paid': 'Opłacono', 'Disputed': 'Sporny', 'PartiallyPaid': 'Częściowo opłacono'
};

export const InvoiceTypeStringMap: Record<string, string> = {
  'Invoice': 'Faktura', 'CreditNote': 'Nota kredytowa', 'DebitNote': 'Nota debetowa'
};

export const ProductStatusMap: Record<number, string> = {
  0: 'Aktywny', 1: 'Nieaktywny', 2: 'Wycofany', 3: 'Oczekuje na zatwierdzenie'
};

export const ChannelMap: Record<number, string> = {
  0: 'Seller Central', 1: 'Vendor'
};

export const UserRoleMap: Record<number, string> = {
  0: 'Admin', 1: 'Manager', 2: 'Operator', 3: 'Viewer'
};

export type AppModule = 'Dashboard' | 'PurchaseOrders' | 'Shipments' | 'Invoices' | 'Products' | 'Inventory' | 'Users' | 'Sync';
export type PermissionLevel = 'None' | 'Read' | 'Write';

export interface UserPermission {
  module: AppModule;
  moduleId: number;
  level: PermissionLevel;
  levelId: number;
}

export const AppModuleLabels: Record<AppModule, string> = {
  Dashboard: 'Dashboard',
  PurchaseOrders: 'Zamówienia (PO)',
  Shipments: 'Wysyłki (ASN)',
  Invoices: 'Faktury',
  Products: 'Produkty',
  Inventory: 'Stany magazynowe',
  Users: 'Użytkownicy',
  Sync: 'Synchronizacja',
};

export const PermissionLevelLabels: Record<PermissionLevel, string> = {
  None: 'Brak',
  Read: 'Odczyt',
  Write: 'Odczyt i zapis',
};
